import {
  Component,
  inject,
  OnInit,
  Signal,
  ViewChild,
  ElementRef,
  signal,
  effect,
  input,
} from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { DestroyableComponent } from '@eda/shared/destroyable';
import { Message } from '@eda/shared/models/message.model';
import { takeUntil, tap, Subject, BehaviorSubject } from 'rxjs';
import { MessageBlock } from './modules/message-block/message-block';
import { EdaApiService } from '@eda/core/services/eda-api-service';
import { SalesforceApiService } from '@eda/core/services/salesforce-api-service';
import { MessageService } from '@eda/core/services/message-service';
import { AsyncPipe } from '@angular/common';
import {
  convertMessageToUserMessage,
  convertMessageEventToMessage,
} from '@eda/shared/utils/message-util';
import { EventMessageType } from '@eda/shared/enums/conversation-type';
import { ProgressBarModule } from 'primeng/progressbar';
import { SuggestedQuestionService } from './services/suggested-question-service';
import { MessageAutoScroll } from '@eda/shared/directives/message-auto-scroll';
import { ChatWelcome } from './modules/chat-welcome/chat-welcome';
import { PromptsDialog } from './modules/prompts-dialog/prompts-dialog';
import { ConversationSummary } from '@eda/shared/models/chat-history.model';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'aiops-messenger',
  imports: [
    ReactiveFormsModule,
    ChatWelcome,
    MessageBlock,
    PromptsDialog,
    AsyncPipe,
    ProgressBarModule,
    MessageAutoScroll,
    TranslatePipe,
  ],
  templateUrl: './messenger.html',
})
export class Messenger extends DestroyableComponent implements OnInit {
  @ViewChild('messageInput') input!: ElementRef<HTMLInputElement>;

  selectedConversation = input<ConversationSummary | null>(null);

  override toBeDestory$ = new Subject<void>();

  private edaApi = inject(EdaApiService);
  private salesforceApi = inject(SalesforceApiService);
  private messageService = inject(MessageService);
  private suggestedQuestionService = inject(SuggestedQuestionService);

  private _isConnected = new BehaviorSubject(false);
  private _agentLoading = new BehaviorSubject(true);

  private streamingMessageId = '';
  private streamingContent = '';
  private streamingUpdateFrameId: number | null = null;

  msg = new FormControl('');

  maxCount = 2000;
  currentMsgCount = 0;
  messageQueue: Signal<Message[]> = this.messageService.retrieveMessages();

  isConnected$ = this._isConnected.asObservable();
  agentLoading = this._agentLoading.asObservable();

  agentTyping = new BehaviorSubject<boolean>(false);

  constructor() {
    super();

    effect(() => {
      const conversation = this.selectedConversation();
      if (conversation?.allMessages && conversation.allMessages.length > 0) {
        this.loadConversationHistory(conversation);
      }
    });
  }

  ngOnInit(): void {
    this.msg.valueChanges.pipe(takeUntil(this.toBeDestory$)).subscribe((value) => {
      if (!value) {
        this.currentMsgCount = 0;
        return;
      }
      if (value.length >= this.maxCount) {
        return;
      }
      this.currentMsgCount = value.length;
    });

    this.edaApi
      .generateConversationSession()
      .pipe(takeUntil(this.toBeDestory$))
      .subscribe((response) => {
        this.salesforceApi
          .initializeSseConnection()
          .pipe(
            takeUntil(this.toBeDestory$),
            tap(() => {
              this._isConnected.next(true);
              this._agentLoading.next(false);
            }),
          )
          .subscribe({
            next: (event) => {
              if (event.type === 'error') {
                const errorEvent = event as ErrorEvent;
              } else {
                const messageEvent = event as MessageEvent;
                this.processMessageEvent(messageEvent);
              }
            },
            error: (err) => {
              console.error('SSE connection error:', err);
              this._agentLoading.next(false);
              this._isConnected.next(false);
            },
          });
      });
    this.suggestedQuestionService.suggestionQuestion
      .pipe(takeUntil(this.toBeDestory$))
      .subscribe((suggested) => {
        if (suggested.autoSend) {
          this.invokeMessageSend(suggested.text);
        } else {
          this.msg.setValue(suggested.text.replace(/\[.*?\]/g, ''));

          setTimeout(() => {
            const input = this.input.nativeElement;
            const length = input.value.length;
            input.setSelectionRange(length, length);
            input.focus();
          }, 0);
        }
      });
  }
  sendMessage() {
    if (!this.msg.value || this.msg.value.trim().length === 0) {
      return;
    }
    const userMsg = this.msg.value;
    this.msg.setValue('');
    this.invokeMessageSend(userMsg);
  }

  invokeMessageSend(userMsg: string) {
    this.edaApi.sendMessage(userMsg).subscribe({
      next: (response) => {
        this.messageService.addToMessage(convertMessageToUserMessage(userMsg));
      },
      error: (err) => {},
    });
  }

  /**
   * Central SSE event dispatcher for chat conversation updates.
   *
   * Functional flow:
   * 1. Ignores heartbeat events.
   * 2. Updates typing/loader indicators based on event type.
   * 3. For streaming tokens, creates a placeholder message first, then appends chunks.
   * 4. For final message events, either finalizes an active stream or stores a non-stream response.
   */
  private processMessageEvent(messageEvent: MessageEvent) {
    if (!messageEvent) {
      throw new Error('Message Event cannot be empty/null');
    }
    switch (messageEvent.type) {
      case 'ping':
        return;
      case EventMessageType[EventMessageType.CONVERSATION_PROGRESS_INDICATOR]:
      case EventMessageType[EventMessageType.CONVERSATION_TYPING_STARTED_INDICATOR]:
        this.agentTyping.next(true);
        return;
      case EventMessageType[EventMessageType.CONVERSATION_STREAMING_TOKEN]:
        if (this.createStreamingMessage()) {
          this.handleStreamingChunk(messageEvent);
        }
        return;
      case EventMessageType[EventMessageType.CONVERSATION_TYPING_STOPPED_INDICATOR]:
        this.agentTyping.next(false);
        return;
      case EventMessageType[EventMessageType.CONVERSATION_ROUTING_RESULT]:
        this._agentLoading.next(true);
        return;
      case EventMessageType[EventMessageType.CONVERSATION_PARTICIPANT_CHANGED]:
        return;
      case EventMessageType[EventMessageType.CONVERSATION_MESSAGE]:
        if (this.streamingMessageId) {
          this.finalizeStreamingMessage();
          return;
        }
        this.agentTyping.next(false);
        this._agentLoading.next(false);
        const agentMessage = JSON.parse(messageEvent.data);
        const messageObject = convertMessageEventToMessage(agentMessage);
        this.edaApi.feedbackIdSubject.next(messageObject.id);
        if (messageObject.sender !== 'Guest' || messageObject.role !== 'EndUser') {
          this.messageService.addToMessage(messageObject);
        }
        return;
      default:
        return;
    }
  }

  /**
   * Ensures a single streaming placeholder message exists before chunk updates begin.
   * Returns true once the streaming message is ready to receive chunk content.
   */
  private createStreamingMessage(): boolean {
    if (this.streamingMessageId) {
      return true;
    }

    const streamingMsg: Message = {
      id: `streaming-${Date.now()}`,
      text: '',
      role: 'Agent',
      sender: 'Agent',
      timestamp: new Date(),
      isOwnMessage: false,
      isStreaming: true,
    };

    this.streamingMessageId = streamingMsg.id;
    this.messageService.addToMessage(streamingMsg);
    return true;
  }

  /**
   * Appends an incoming streaming token to the current streaming message.
   * This should only be called after createStreamingMessage has completed.
   */
  private handleStreamingChunk(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);

      if (data.conversationEntry.entryType === 'StreamingToken') {
        this.agentTyping.next(false);
        const entryPayload = JSON.parse(data.conversationEntry.entryPayload);
        const chunk = entryPayload.streamingToken.token.text;

        this.streamingContent += chunk;
        this.scheduleStreamingMessageUpdate();
      }
    } catch (e) {
      console.error('Streaming chunck error:', e);
    }
  }

  /**
   * Schedules a single UI update on the next paint frame.
   *
   * Functional behavior:
   * 1. Drops scheduling when there is no active streaming message.
   * 2. Coalesces many incoming chunks into one render per frame.
   * 3. Keeps latest accumulated content for the next push operation.
   */
  private scheduleStreamingMessageUpdate(): void {
    if (!this.streamingMessageId || this.streamingUpdateFrameId !== null) {
      return;
    }

    this.streamingUpdateFrameId = requestAnimationFrame(() => {
      this.streamingUpdateFrameId = null;
      this.pushStreamingMessageUpdate(true);
    });
  }

  /**
   * Pushes the latest buffered streaming content to the message store.
   *
   * @param isStreaming True while tokens are still arriving; false for final flush.
   */
  private pushStreamingMessageUpdate(isStreaming: boolean): void {
    if (!this.streamingMessageId) {
      return;
    }

    const updatedMsg: Message = {
      id: this.streamingMessageId,
      text: this.streamingContent,
      role: 'Agent',
      sender: 'Agent',
      timestamp: new Date(),
      isOwnMessage: false,
      isStreaming,
    };

    this.messageService.updateMessage(updatedMsg);
  }

  /**
   * Marks the active streaming message as complete and clears streaming state.
   * Loader is stopped only here so it reflects full-message completion.
   */
  private finalizeStreamingMessage(): void {
    if (!this.streamingMessageId) return;

    if (this.streamingUpdateFrameId !== null) {
      cancelAnimationFrame(this.streamingUpdateFrameId);
      this.streamingUpdateFrameId = null;
    }

    this.pushStreamingMessageUpdate(false);
    this.streamingMessageId = '';
    this.streamingContent = '';
    this._agentLoading.next(false);
  }

  private loadConversationHistory(conversation: ConversationSummary) {
    this.messageService.clearMessages();

    const uniqueMessages = conversation
      .allMessages!.map((item: any, index: number) => ({ item, index }))
      .filter(({ item }, idx, arr) => {
        const prevIdx = arr.findIndex(
          (obj, i) =>
            i < idx &&
            obj.item.ssot__PayloadText__c === item.ssot__PayloadText__c &&
            obj.item.ssot__ParticipantDisplayName__c === item.ssot__ParticipantDisplayName__c,
        );
        return prevIdx === -1;
      });

    const messages: Message[] = uniqueMessages
      .map((item: any, index: number) => {
        try {
          const payload = JSON.parse(item.item.ssot__PayloadText__c);
          const text = this.extractMessageText(payload);
          const isUser = item.item.ssot__ParticipantDisplayName__c === 'Guest';
          const uniqueId = `${conversation.conversationId}-${item.item.ssot__CreatedDate__c}-${index}`;

          return {
            id: uniqueId,
            text: text,
            role: isUser ? 'EndUser' : 'Agent',
            sender: isUser ? 'Guest' : 'Eaton Digital Assistant',
            timestamp: new Date(item.item.ssot__CreatedDate__c),
            isOwnMessage: isUser,
            isStreaming: false,
          } as Message;
        } catch (e) {
          console.warn('Error processing message:', e);
          return null as any;
        }
      })
      .filter(Boolean) as Message[];

    messages.forEach((msg) => {
      this.messageService.addToMessage(msg);
    });
  }

  private extractMessageText(payload: any): string {
    try {
      if (payload?.abstractMessage?.staticContent?.text) {
        return payload.abstractMessage.staticContent.text;
      }
      return payload.text || JSON.stringify(payload).substring(0, 100) + '...';
    } catch {
      return 'Unable to process message';
    }
  }
}
